/* The Kashira — search overlay enhancements (spec 10).
   Extends Dawn's <predictive-search> rather than replacing it: Dawn keeps
   ownership of fetching, rendering, aria-activedescendant and the live-region
   announcements. This layer adds:
     1. a <2-character gate (Dawn fetches from 1 char) so the panel shows
        RECENT SEARCHES instead of firing a request
     2. recent-query persistence in localStorage (the visitor's own history)
     3. ESC that closes the panel and keeps focus in the input
     4. arrow-key traversal while the recent panel is the visible listbox
     5. recording the term whenever a search is actually submitted
   Every list item it injects is a role="option" <li>, which is exactly what
   Dawn's switchOption()/selectOption() already traverse — no keyboard model
   is duplicated here. */

(() => {
  const STORAGE_KEY = 'kashira:recent-searches';
  const CAP = 5;
  const MIN_CHARS = 2;

  const RecentSearches = {
    read() {
      try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((term) => typeof term === 'string' && term.trim().length >= MIN_CHARS).slice(0, CAP);
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, CAP)));
      } catch (e) {
        /* private mode / quota — recent searches are a nicety, never a blocker */
      }
    },
    add(term) {
      const clean = (term || '').trim();
      if (clean.length < MIN_CHARS) return;
      const list = this.read().filter((entry) => entry.toLowerCase() !== clean.toLowerCase());
      list.unshift(clean);
      this.write(list);
    },
    clear() {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* no-op */
      }
    },
  };

  window.KashiraRecentSearches = RecentSearches;

  /* ---------------------------------------------------------------------
     Record the term on any real search submission (header form, template
     form, or the overlay's VIEW ALL / "search for" row).
     --------------------------------------------------------------------- */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.getAttribute('role') !== 'search') return;
      const input = form.querySelector('input[type="search"]');
      if (input) RecentSearches.add(input.value);
    },
    true
  );

  /* --------------------------------------------------------------------- */

  class RecentPanel {
    constructor(host) {
      this.host = host;
      this.input = host.querySelector('input[type="search"]');
      this.root = host.querySelector('[data-kashira-recent]');
      this.list = this.root?.querySelector('[data-kashira-recent-list]');
      this.template = this.root?.querySelector('[data-kashira-recent-template]');
      this.clearButton = this.root?.querySelector('[data-kashira-recent-clear]');
      this.searchUrl = this.root?.dataset.searchUrl || '/search';
      this.idPrefix = this.list?.dataset.idPrefix || 'kashira-recent-option';
      this.visible = false;

      if (!this.input || !this.root || !this.list || !this.template) return;

      /* Dawn's combobox points the input at #predictive-search-results. While
         the recent panel is the visible popup the input must own THIS listbox
         instead, or aria-activedescendant would reference an id that lives in a
         listbox the input does not own. Remember the original so results can
         take ownership straight back. */
      this.resultsListboxId = this.input.getAttribute('aria-owns') || 'predictive-search-results';
      this.recentListboxId = this.list.id;
      this.status = this.host.querySelector('.predictive-search-status');

      this.clearButton?.addEventListener('click', () => {
        RecentSearches.clear();
        this.hide();
        this.host.close?.();
        this.input.focus();
      });

      this.input.addEventListener('focus', () => this.sync());
      this.input.addEventListener('input', () => this.sync());
      this.host.addEventListener('keydown', this.onKeydown.bind(this));
      this.host.addEventListener('keyup', this.onKeyup.bind(this), true);
      this.ready = true;
    }

    /* Show recents whenever the query is below the predictive threshold. */
    sync() {
      if (!this.ready) return;
      if (this.input.value.trim().length >= MIN_CHARS) {
        this.hide();
        return;
      }
      this.show();
    }

    show() {
      const terms = RecentSearches.read();
      if (!terms.length) {
        this.hide();
        return;
      }

      this.list.textContent = '';
      terms.forEach((term, index) => {
        const item = this.template.content.firstElementChild.cloneNode(true);
        item.id = `${this.idPrefix}-${index + 1}`;
        item.setAttribute('aria-selected', 'false');
        const link = item.querySelector('a');
        link.href = `${this.searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        item.querySelector('.kashira-recent__term').textContent = term;
        link.addEventListener('click', () => RecentSearches.add(term));
        this.list.appendChild(item);
      });

      const alreadyVisible = this.visible;

      this.root.hidden = false;
      this.visible = true;
      this.host.setAttribute('open', 'true');
      this.host.setAttribute('data-kashira-recent-open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.own(this.recentListboxId);

      // Match how Dawn announces its result count, so opening the recent panel
      // is not a silent change of popup for a screen-reader user.
      if (!alreadyVisible) this.announce(terms.length);
    }

    hide() {
      if (!this.ready) return;
      this.root.hidden = true;
      this.visible = false;
      this.host.removeAttribute('data-kashira-recent-open');
      if (this.host.getAttribute('results') !== 'true') {
        this.host.removeAttribute('open');
        this.input.setAttribute('aria-expanded', 'false');
      }
      this.clearSelection();
      // Results own the popup again the moment recents step aside.
      this.own(this.resultsListboxId);
    }

    /* Keep aria-owns and aria-controls on the listbox that is actually showing. */
    own(listboxId) {
      if (!listboxId) return;
      this.input.setAttribute('aria-owns', listboxId);
      this.input.setAttribute('aria-controls', listboxId);
    }

    announce(count) {
      if (!this.status) return;
      const template = count === 1 ? this.list.dataset.announceOne : this.list.dataset.announceOther;
      if (!template) return;
      const text = template.replace('[count]', count);
      // Reuse Dawn's own live-region routine (it flips aria-hidden around the
      // update) so recents announce exactly the way result counts do.
      if (typeof this.host.setLiveRegionText === 'function') {
        this.host.statusElement = this.host.statusElement || this.status;
        this.host.setLiveRegionText(text);
        return;
      }
      this.status.textContent = text;
    }

    clearSelection() {
      this.list.querySelectorAll('[aria-selected="true"]').forEach((el) => el.setAttribute('aria-selected', 'false'));
      this.input.setAttribute('aria-activedescendant', '');
    }

    onKeydown(event) {
      if (!this.visible) return;
      /* Stop the caret jumping to either end of the input while traversing. */
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
    }

    /* Runs in the capture phase, before Dawn's own keyup handler, so that an
       empty query does not get close(true)'d out from under the recent list. */
    onKeyup(event) {
      if (!this.visible) return;
      const items = Array.from(this.list.children);
      if (!items.length) return;

      if (event.code === 'Escape') return;
      /* Dawn's own keyup handler close(true)s on an empty query, which would
         tear the recent list down as soon as it appeared. While the recent
         panel is the visible listbox this layer owns the keys instead. */
      event.stopPropagation();
      if (event.code !== 'ArrowUp' && event.code !== 'ArrowDown' && event.code !== 'Enter') return;

      const current = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');

      if (event.code === 'Enter') {
        if (current > -1) {
          event.preventDefault();
          items[current].querySelector('a')?.click();
        }
        return;
      }

      let next;
      if (event.code === 'ArrowDown') {
        next = current === -1 || current === items.length - 1 ? 0 : current + 1;
      } else {
        next = current <= 0 ? items.length - 1 : current - 1;
      }

      if (current > -1) items[current].setAttribute('aria-selected', 'false');
      items[next].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', items[next].id);
      items[next].scrollIntoView({ block: 'nearest' });
    }
  }

  /* ---------------------------------------------------------------------
     Patch PredictiveSearch: gate the fetch at MIN_CHARS and hand the panel
     over to recents below it. Everything else stays Dawn's.
     --------------------------------------------------------------------- */
  const PredictiveSearch = window.customElements.get('predictive-search');

  if (PredictiveSearch && !PredictiveSearch.prototype.__kashiraPatched) {
    PredictiveSearch.prototype.__kashiraPatched = true;

    /* Below the threshold, short-circuit before Dawn's close(true) — which
       would blank the input — and show recents instead. */
    const originalOnChange = PredictiveSearch.prototype.onChange;
    PredictiveSearch.prototype.onChange = function () {
      const term = this.getQuery();
      if (term.length >= MIN_CHARS) {
        originalOnChange.call(this);
        return;
      }
      this.toggleResetButton();
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      this.searchTerm = term;
      this.removeAttribute('results');
      this.removeAttribute('loading');
      this.kashiraRecent?.sync();
    };

    const originalGetSearchResults = PredictiveSearch.prototype.getSearchResults;
    PredictiveSearch.prototype.getSearchResults = function (searchTerm) {
      if (!searchTerm || searchTerm.trim().length < MIN_CHARS) {
        this.removeAttribute('results');
        this.removeAttribute('loading');
        if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = '';
        this.kashiraRecent?.sync();
        return undefined;
      }
      this.kashiraRecent?.hide();
      return originalGetSearchResults.call(this, searchTerm);
    };

    /* The mobile takeover is ONE scrolling sheet (spec 10 state 4). Dawn's
       open() caps the results pane with an inline maxHeight, which beats the
       CSS `max-height: none` and traps long result sets in a nested scroll
       box. Clear the cap — but only for the header takeover: the /search
       template keeps a second PredictiveSearch whose anchored popup still
       needs Dawn's cap. Clearing after open (not skipping) keeps closeResults'
       removeAttribute('style') contract intact. */
    const originalOpen = PredictiveSearch.prototype.open;
    PredictiveSearch.prototype.open = function () {
      originalOpen.call(this);
      if (window.matchMedia('(max-width: 749px)').matches && this.closest('.header__search')) {
        this.predictiveSearchResults.style.maxHeight = '';
      }
    };

    const originalClose = PredictiveSearch.prototype.close;
    PredictiveSearch.prototype.close = function (clearSearchTerm = false) {
      originalClose.call(this, clearSearchTerm);
      if (this.kashiraRecent?.ready) {
        this.kashiraRecent.root.hidden = true;
        this.kashiraRecent.visible = false;
        this.removeAttribute('data-kashira-recent-open');
        this.kashiraRecent.clearSelection();
        // Dawn's close() leaves the combobox pointing at the results listbox;
        // hand ownership back so the two states never disagree.
        this.kashiraRecent.own(this.kashiraRecent.resultsListboxId);
      }
    };
  }

  /* TAB jumps between the overlay's columns (spec 10). Dawn's ArrowUp/Down
     already walks every option in order; TAB skips to the head of the next
     group. Tabbing past the last group is intentionally NOT trapped — focus
     leaves the overlay normally, so the keyboard is never a dead end. */
  const onTab = (event) => {
    if (event.code !== 'Tab' && event.key !== 'Tab') return;
    const host = event.target.closest?.('predictive-search');
    if (!host || !host.hasAttribute('open') || host.getAttribute('results') !== 'true') return;

    const input = host.querySelector('input[type="search"]');
    const groups = Array.from(host.querySelectorAll('.predictive-search__result-group, .kashira-predictive-viewall'))
      .map((group) => Array.from(group.querySelectorAll('li[role="option"]')).filter((li) => li.offsetParent !== null))
      .filter((options) => options.length);
    if (!groups.length) return;

    const selected = host.querySelector('li[role="option"][aria-selected="true"]');
    const currentGroup = selected ? groups.findIndex((options) => options.includes(selected)) : -1;
    const nextGroup = event.shiftKey ? currentGroup - 1 : currentGroup + 1;

    /* Off either end: let the browser move focus out of the overlay. */
    if (nextGroup < 0 || nextGroup >= groups.length) return;

    event.preventDefault();
    if (selected) selected.setAttribute('aria-selected', 'false');
    const target = groups[nextGroup][0];
    target.setAttribute('aria-selected', 'true');
    input?.setAttribute('aria-activedescendant', target.id);
    target.scrollIntoView({ block: 'nearest' });
  };

  /* ESC: close the panel first and keep focus in the input (spec 10).
     A second ESC falls through to Dawn's details-modal, which closes the
     overlay and returns focus to the header toggle. */
  const onEscape = (event) => {
    if (event.code !== 'Escape' && event.key !== 'Escape') return;
    const host = event.target.closest?.('predictive-search');
    if (!host || !host.hasAttribute('open')) return;
    event.stopPropagation();
    host.close?.();
    host.querySelector('input[type="search"]')?.focus();
  };

  const enhance = (host) => {
    if (host.kashiraRecent) return;
    host.kashiraRecent = new RecentPanel(host);
    host.addEventListener('keydown', onEscape, true);
    host.addEventListener('keydown', onTab, true);
  };

  /* ---------------------------------------------------------------------
     Prefix parity between the overlay and the results page.

     The two surfaces run different engines. The overlay uses the Predictive
     Search API, which is built for typeahead and matches "banar" against
     "Banarasi". The results page runs storefront search, where — despite
     `options[prefix]=last` — a partial term matches article bodies but not
     product titles: measured on this store, `banar` returns 0 products while
     `banar*` returns 2. So a shopper who saw two sarees in the overlay and
     pressed enter landed on a page without them.

     Appending the wildcard to the final term on submit makes the page agree
     with the overlay. The term keeps its `*` in the URL so tab links and
     pagination stay prefix-matched; the template strips it back off for
     everything a human reads (see `display_terms` in main-search.liquid).

     Left alone: quoted phrases and terms already carrying an operator, where
     the shopper has expressed an exact intent. --------------------------- */
  const PREFIXABLE = /^[\p{L}\p{N}][\p{L}\p{N}'’-]*$/u;

  const withWildcard = (value) => {
    const query = value.trim();
    if (!query || query.includes('"')) return query;
    const parts = query.split(/\s+/);
    const last = parts[parts.length - 1];
    if (last.length < 2 || !PREFIXABLE.test(last)) return query;
    parts[parts.length - 1] = `${last}*`;
    return parts.join(' ');
  };

  const onSearchSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/search')) return;
    const input = form.querySelector('input[type="search"][name="q"]');
    if (!input) return;
    input.value = withWildcard(input.value);
  };

  document.addEventListener('submit', onSearchSubmit, true);

  /* The overlay's VIEW ALL is an anchor, not a submit — give it the same
     treatment at click time so both routes to the page behave alike. */
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('.kashira-predictive-viewall__item a[href*="/search"]');
      if (!link) return;
      const url = new URL(link.href, window.location.origin);
      const q = url.searchParams.get('q');
      if (!q) return;
      url.searchParams.set('q', withWildcard(q));
      link.href = url.pathname + url.search;
    },
    true
  );

  /* ---------------------------------------------------------------------
     Scroll lock while the overlay is open.

     Deliberately NOT `overflow: hidden` on the root. That is how the filter
     drawer locks, but it breaks `position: sticky`: with the root non-
     scrollable the sticky header drops back to its document position, so
     opening search halfway down a page made the whole header vanish upward
     (measured: modal top -786 at scrollY 900). The header has to keep
     sticking, because the sheet hangs off it.

     So the gesture is refused instead of the scrollport being removed. That
     also leaves the scrollbar in place, so nothing shifts sideways on open.

     Watching the `open` attribute rather than wrapping DetailsModal covers
     every route in and out: the toggle, ESC, the scrim, CANCEL, and the
     browser restoring an open <details>. ------------------------------- */
  const isOverlayOpen = () => !!document.querySelector('.header__search details[open]');

  /* Refuse the gesture unless it lands on something inside the overlay that
     can still scroll — the panel does overflow on short screens. */
  const scrollableInOverlay = (target, deltaY) => {
    const modal = target instanceof Element ? target.closest('.search-modal') : null;
    if (!modal) return false;
    let el = target;
    while (el && el !== modal.parentElement) {
      if (el instanceof Element) {
        const style = getComputedStyle(el);
        if (/auto|scroll/.test(style.overflowY)) {
          const room = el.scrollHeight - el.clientHeight;
          if (room > 1 && (deltaY > 0 ? el.scrollTop < room - 1 : el.scrollTop > 1)) return true;
        }
      }
      el = el.parentElement;
    }
    return false;
  };

  const blockGesture = (event) => {
    if (!document.querySelector('.header__search details[open]')) return;
    const deltaY = event.type === 'wheel' ? event.deltaY : 1;
    if (scrollableInOverlay(event.target, deltaY)) return;
    if (event.cancelable) event.preventDefault();
  };

  const watchOpenState = (details) => {
    if (details.dataset.kashiraScrollLock === 'bound') return;
    details.dataset.kashiraScrollLock = 'bound';
    const sync = () => document.documentElement.toggleAttribute('data-kashira-search-open', details.hasAttribute('open'));
    new MutationObserver(sync).observe(details, { attributes: true, attributeFilter: ['open'] });
    sync();
  };

  const SCROLL_KEYS = new Set(['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', ' ', 'Spacebar']);

  const blockScrollKey = (event) => {
    if (!isOverlayOpen() || !SCROLL_KEYS.has(event.key)) return;
    // Never steal keys from the query field or from list navigation.
    const t = event.target;
    if (t instanceof Element && (t.closest('.search-modal') || t.matches('input, textarea, select'))) return;
    event.preventDefault();
  };

  window.addEventListener('wheel', blockGesture, { passive: false });
  window.addEventListener('touchmove', blockGesture, { passive: false });
  window.addEventListener('keydown', blockScrollKey);

  const initScrollLock = () =>
    document.querySelectorAll('.header__search details').forEach(watchOpenState);

  const init = () => {
    document.querySelectorAll('predictive-search').forEach(enhance);
    initScrollLock();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Sections re-render in the theme editor and on cart/search updates. */
  document.addEventListener('shopify:section:load', init);
})();
